import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@workos-inc/authkit-react";
import { Settings, LogOut, MessageSquare } from "lucide-react";
// TODO: re-enable when admin role switching is wired up
// import { CircleHelp, Megaphone } from "lucide-react";
// import { Tabs, TabsList, TabsTrigger } from "@/v2/components/ui/tabs";
import { BrandLogoFull } from "@/v2/components/BrandLogo";
import { Avatar, AvatarFallback } from "@/v2/components/ui/avatar";
import { Popover, PopoverTrigger, PopoverContent } from "@/v2/components/ui/popover";
import { cn } from "@/v2/lib/utils";

const CAL_BASE = "https://cal.com/team/hourglass/hourglass-1-1-support-call";

interface MobileHeaderProps {
	userInitials: string;
	userDisplayName: string;
	userEmail?: string;
	showRoleSwitcher?: boolean;
	currentRole?: "attorney" | "admin";
	onRoleSwitch?: () => void;
}

export function MobileHeader({
	userInitials,
	userDisplayName,
	userEmail,
	showRoleSwitcher: _showRoleSwitcher,
	currentRole: _currentRole = "attorney",
	onRoleSwitch: _onRoleSwitch,
}: MobileHeaderProps) {
	const navigate = useNavigate();
	const location = useLocation();
	const { signOut } = useAuth();
	const nav = (path: string) => navigate(path + location.search);

	const calLink = (() => {
		const params = new URLSearchParams();
		if (userDisplayName) params.set("name", userDisplayName);
		if (userEmail) params.set("email", userEmail);
		const qs = params.toString();
		return qs ? `${CAL_BASE}?${qs}` : CAL_BASE;
	})();

	const menuItems = [
		{ label: "Settings", icon: Settings, path: "/settings" },
		/* hourglass:mobile:v0 — disabled for mobile v0
		{ label: "FAQ", icon: CircleHelp, path: "/faq" },
		{ label: "Updates", icon: Megaphone, path: "/updates" },
		*/
	];

	return (
		<header className="shrink-0 flex h-14 items-center justify-between border-b border-border bg-background px-4">
			<BrandLogoFull className="h-6 w-auto text-foreground" aria-label="Hourglass" />

			<Popover>
				<PopoverTrigger asChild>
					<button className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
						<Avatar className="h-9 w-9">
							<AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
								{userInitials}
							</AvatarFallback>
						</Avatar>
					</button>
				</PopoverTrigger>
				<PopoverContent>
					<div className="px-1 pb-1">
						<p className="text-sm font-medium truncate">{userDisplayName}</p>
						{userEmail && (
							<p className="text-xs text-muted-foreground truncate">{userEmail}</p>
						)}
					</div>

					<div className="flex flex-col gap-0.5 mt-2">
						{menuItems.map((item) => {
							const isActive = location.pathname === item.path;
							return (
								<button
									key={item.path}
									onClick={() => nav(item.path)}
									className={cn(
										"flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors",
										isActive
											? "bg-accent text-accent-foreground"
											: "hover:bg-accent",
									)}
								>
									<item.icon className="size-4" />
									{item.label}
								</button>
							);
						})}
						<a
							href={calLink}
							target="_blank"
							rel="noopener noreferrer"
							className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors hover:bg-accent"
						>
							<MessageSquare className="size-4" />
							Support
						</a>
						<div className="my-1 h-px bg-border" />
						<button
							onClick={() => signOut({ returnTo: window.location.origin })}
							className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors hover:bg-accent"
						>
							<LogOut className="size-4" />
							Sign out
						</button>
					</div>

					{/* hourglass:mobile:v0 — role switcher disabled for mobile v0
					{showRoleSwitcher && (
						<Tabs
							className="mt-2"
							value={currentRole}
							onValueChange={(value) => {
								if (value !== currentRole) onRoleSwitch?.();
							}}
						>
							<TabsList>
								<TabsTrigger value="attorney">Timekeeper</TabsTrigger>
								<TabsTrigger value="admin">Admin</TabsTrigger>
							</TabsList>
						</Tabs>
					)}
					*/}
				</PopoverContent>
			</Popover>
		</header>
	);
}
